<template>
  <div>
    <div class='py-2'>
      <div class='flex flex-row justify-start items-center gap-x-2 text-xs'>
        <div class='font-bold'>
          {{comment.name}}
        </div>
        <div class=''>
          {{commentDateTimeString}}
        </div>
      </div>
      <div v-if='!isParentEditingMode'
        class='mt-1 flex flex-row justify-between gap-x-4 break-all'>
        <div>
          <div>{{ comment.body }}</div>
          <div v-if='translatedText' class='mt-1 text-sm text-gray-700 bg-gray-100 p-1 rounded'>
            {{ translatedText }}
          </div>
        </div>
        <div class='flex-shrink-0'>
          <button @click='openParentEditingInput()' v-if='allowEditing' class='mr-1'>
            <pencil-alt-icon size='1.2x'/>
          </button>
          <button @click='removeComment(comment.id)' v-if='allowDeleting'>
            <trash-icon size='1.2x'/>
          </button>
        </div>
      </div>
      <comment-edit-input
        v-else
        @close-edit-input='closeParentEditingInput()'
        :editing-comment-id='comment.id'
        :editing-comment-body='comment.body'
        editing-comment-parent-comment-id=''/>
      <div class='flex flex-row justify-start items-center gap-x-2 mt-1'>
          <button @click='translateComment' class='text-xs capitalize border rounded hover:bg-gray-100 hover:border-blue-300 px-2 py-1'>
            {{ isTranslating ? 'Translating...' : 'Translate' }}
          </button>
        <button
          @click='openReply'
          class='text-xs capitalize border rounded hover:bg-gray-100 hover:border-blue-300 px-2 py-1'>
          {{ replyButtonText }}
        </button>
      </div>
    </div>
    <div class='pl-10 bg-gray-50 border-t border-b'>
      <div v-for='(reply, index) in replyCommentsByParentCommentId(comment.id)'
        :key='`reply-${index}`'>
        <comment-reply-row 
          :reply='reply' 
          :session='session' />
      </div>
      <div v-if='showReplyCommentInput'>
        <comment-create-input :parent-comment-id='comment.id' />
        <button
          class='w-full py-1 text-center text-sm uppercase font-medium'
          @click='closeReply'>
          {{ hideButtonText }}
          <chevron-up-icon class='inline-block' size='1.2x' />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CommentCreateInput from '@/components/gbcc2025/PublicSessionCommentCreateInput.vue'
import CommentEditInput   from '@/components/gbcc2025/PublicSessionCommentEditInput.vue'
import CommentReplyRow    from '@/components/gbcc2025/PublicSessionCommentReplyRow.vue'
import DateTimeStringHelpers from '@/utils/date-time-string-helpers'
import { TrashIcon, PencilAltIcon, ChevronUpIcon } from '@vue-hero-icons/outline'
import { mapState, mapGetters, mapActions } from 'vuex'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)

export default {
  name: 'PublicSessionCommentRow',
  props: ['comment', 'session'],
  data() {
    return {
      showReplyCommentInput: false,
      isParentEditingMode: false,
      translatedText: null,
      isTranslating: false,
    }
  },
  components: {
    CommentCreateInput,
    CommentEditInput,
    CommentReplyRow,
    TrashIcon,
    PencilAltIcon,
    ChevronUpIcon
  },
  computed: {
    ...mapState('comments', [
      'comments',
      'instance',
    ]),
    ...mapGetters('posters', [
      'selectedPosterCommentReplies',
    ]),
    ...mapGetters('events', [
      'showTextInKorean',
    ]),
    ...mapGetters('users', [
      'isConferenceSpeaker',
      'isAdmin',
    ]),
    commentDateTimeString () {
      return DateTimeStringHelpers.toDateTimShort(this.comment.createdAt)
    },
    // sameOrAfterVideoScheduledStartDate () {
    //   return dayjs().tz('Asia/Seoul').isSameOrAfter(dayjs(this.session.scheduledStartTime).tz('Asia/Seoul'), 'date')
    // },
    // sameVideoScheduledStartDate () {
    //   return dayjs().tz('Asia/Seoul').isSame(dayjs(this.session.scheduledStartTime).tz('Asia/Seoul'), 'date')
    // },
    allowDeleting () { // going to hide these controls for now
      return this.isConferenceSpeaker || this.isAdmin
    },
    allowEditing () { // going to hide these controls for now
      return this.isConferenceSpeaker || this.isAdmin
    },
    replyButtonText () {
      if (this.showTextInKorean) {
        return '댓글'
      } else {
        return 'Reply'
      }
    },
    hideButtonText () {
      if (this.showTextInKorean) {
        return '숨기기'
      } else {
        return 'Hide'
      }
    },
    browserLanguage() {
      return navigator.language || 'en-US'
    },
    deleteCommentConfirmQuestionText () {
      if (this.showTextInKorean) {
        return '질의 사항을 삭제하시겠습니까?'
      } else {
        return 'Are you sure you want delete this question?'
      }
    },
    deleteConfirmMessageText () {
      if (this.showTextInKorean) {
        return '질의 사항이 삭제되었습니다.'
      } else {
        return 'This question has been successfully deleted.'
      }
    },
    confirmDeleteButtonText () {
      if (this.showTextInKorean) {
        return '예, 삭제합니다'
      } else {
        return 'Delete'
      }
    },
    dontDeleteButtonText () {
      if (this.showTextInKorean) {
        return '아니오'
      } else {
        return 'Keep Question'
      }
    },
  },
  methods: {
    ...mapActions('comments', [
      'deactivateComment',
    ]),
    async translateComment() {
      this.isTranslating = true
      this.translatedText = null

      const payload = {
        model: "gpt-4o",
        messages: [
          {
            role: "system",
            content:
              "You are a translator. If there is a medical terms in english, please also write original term in parentesis",
          },
          {
            role: "user",
            content: `Translate this to ${this.browserLanguage}: ${this.comment.body}`,
          },
        ],
      }

      try {
        const response = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.VUE_APP_OPENAI_API_KEY}`, // Replace securely
          },
          body: JSON.stringify(payload),
        })

        const result = await response.json()
        this.translatedText = result.choices?.[0]?.message?.content || "No translation available."
      } catch (err) {
        console.error(err)
        this.translatedText = "Translation failed."
      } finally {
        this.isTranslating = false
      }
    },
    replyCommentsByParentCommentId (parentCommentId) {
      return this.instance.type === 'Session' 
        ? this.comments.filter(comment => comment.parentCommentId === parentCommentId) 
        : this.selectedPosterCommentReplies.filter(comment => comment.parentCommentId === parentCommentId)
    },
    openReply () {
      this.showReplyCommentInput = true
    },
    closeReply () {
      this.showReplyCommentInput = false
    },
    openParentEditingInput () {
      this.isParentEditingMode = true
    },
    closeParentEditingInput () {
      this.isParentEditingMode = false
    },
    removeComment (commentId) {
      this.$confirm(this.deleteCommentConfirmQuestionText, {
        confirmButtonText: this.confirmDeleteButtonText,
        cancelButtonText: this.dontDeleteButtonText,
        type: 'warning'
      }).then(() => {
        this.deactivateComment(commentId).then(() => {
          this.closeReply()
          this.$message({
            type: 'success',
            message: this.deleteConfirmMessageText
          })
        })
      }).catch(() => {})
    }
  }
}
</script>
